import { useState } from "react";
import type { Question } from "../../lib/wizard.js";
import { MedicationsField } from "./MedicationsField.js";

export function QuestionField({
  question,
  value,
  onChange
}: {
  question: Question;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  switch (question.type) {
    case "text":
      return (
        <input
          className="input"
          value={(value as string) ?? ""}
          placeholder={question.placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case "longtext":
      return (
        <textarea
          className="textarea"
          value={(value as string) ?? ""}
          placeholder={question.placeholder}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
        />
      );

    case "number":
      return (
        <div className="row">
          <input
            className="input"
            type="number"
            inputMode="decimal"
            min={question.min}
            max={question.max}
            step={question.step ?? 1}
            value={value === undefined || value === null ? "" : (value as number)}
            placeholder={question.placeholder}
            onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
            style={{ maxWidth: 160 }}
          />
          {question.unit && <span className="dim">{question.unit}</span>}
        </div>
      );

    case "weight":
      return (
        <div className="row">
          <input
            className="input"
            type="number"
            inputMode="decimal"
            min={50}
            max={800}
            value={value === undefined || value === null ? "" : (value as number)}
            onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
            style={{ maxWidth: 160 }}
          />
          <span className="dim">lb</span>
        </div>
      );

    case "height":
      return <HeightField value={value as number | null} onChange={onChange} />;

    case "date":
      return (
        <input
          className="input"
          type="date"
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
          style={{ maxWidth: 220 }}
        />
      );

    case "single":
      return (
        <div className="choice-grid">
          {question.options?.map((opt) => (
            <button
              type="button"
              key={opt.value}
              className={`choice${value === opt.value ? " selected" : ""}`}
              onClick={() => onChange(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      );

    case "multi": {
      const selected: string[] = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div className="choice-grid">
          {question.options?.map((opt) => {
            const on = selected.includes(opt.value);
            return (
              <button
                type="button"
                key={opt.value}
                className={`choice${on ? " selected" : ""}`}
                onClick={() =>
                  onChange(
                    on ? selected.filter((v) => v !== opt.value) : [...selected, opt.value]
                  )
                }
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      );
    }

    case "scale": {
      const min = question.min ?? 1;
      const max = question.max ?? 5;
      const items = Array.from({ length: max - min + 1 }, (_, i) => min + i);
      return (
        <div className="scale-row">
          {items.map((n) => (
            <button
              type="button"
              key={n}
              className={`scale-dot${value === n ? " selected" : ""}`}
              onClick={() => onChange(n)}
              aria-label={String(n)}
            >
              {n}
            </button>
          ))}
        </div>
      );
    }

    case "boolean":
      return (
        <div className="choice-grid">
          {[
            { v: true, l: "Yes" },
            { v: false, l: "No" }
          ].map((o) => (
            <button
              type="button"
              key={o.l}
              className={`choice${value === o.v ? " selected" : ""}`}
              onClick={() => onChange(o.v)}
            >
              {o.l}
            </button>
          ))}
        </div>
      );

    case "medications":
      return (
        <MedicationsField
          value={Array.isArray(value) ? (value as Record<string, unknown>[]) : []}
          onChange={onChange}
        />
      );

    default:
      return null;
  }
}

function HeightField({
  value,
  onChange
}: {
  value: number | null;
  onChange: (value: unknown) => void;
}) {
  const totalInches = typeof value === "number" ? value : null;
  const [ft, setFt] = useState(totalInches !== null ? Math.floor(totalInches / 12) : "");
  const [inch, setInch] = useState(
    totalInches !== null ? Math.round(totalInches - Math.floor(totalInches / 12) * 12) : ""
  );

  function update(nextFt: number | string, nextIn: number | string) {
    const f = Number(nextFt);
    const i = Number(nextIn);
    if (Number.isFinite(f) && nextFt !== "") {
      onChange(f * 12 + (Number.isFinite(i) && nextIn !== "" ? i : 0));
    } else {
      onChange(null);
    }
  }

  return (
    <div className="row">
      <input
        className="input"
        type="number"
        inputMode="numeric"
        min={3}
        max={8}
        value={ft}
        onChange={(e) => {
          setFt(e.target.value);
          update(e.target.value, inch);
        }}
        style={{ maxWidth: 90 }}
      />
      <span className="dim">ft</span>
      <input
        className="input"
        type="number"
        inputMode="numeric"
        min={0}
        max={11}
        value={inch}
        onChange={(e) => {
          setInch(e.target.value);
          update(ft, e.target.value);
        }}
        style={{ maxWidth: 90 }}
      />
      <span className="dim">in</span>
    </div>
  );
}
